import DOMPurify from "dompurify";
import { useCartContext } from "../Contexts/CartContext";
import { PRODUCT_TYPE_COMPOSITE } from "./Constants/Product";
import { ORDER_TYPE_DELIVERY, ORDER_TYPE_TAKE_AWAY, ORDER_TYPE_DINE_IN, ORDER_TYPE_SCHEDULING, ORDER_TYPE_STORE_COUNTER, ORDER_TYPE_DIRECT_SALE } from "./Constants/Service";
import { formatZipcode } from "./Formatters";
import { useOrderTypeContext } from "../Contexts/OrderTypeContext";

//* Vibração para 'confirmar' ações de cliques.
export const vibrate = (pattern: number = 30) => {
    if (navigator.vibrate){
        navigator.vibrate(pattern)
    }
}

export const isPhoneValid = (phone: string) => {
    return Boolean( phone.length && phone.length === 11)
}

export const isCpfValid = (cpf: string) => {
    return Boolean( cpf.length && cpf.length === 11)
}

export const useQuantityProductInCartConference = (productId: number): number => {
    const { cartConference, currentCart } = useCartContext();
    const { isStoreCounter } = useOrderTypeContext();

    const cartTarget = isStoreCounter ? cartConference : currentCart?.items;
    if (!cartTarget || (cartTarget && cartTarget.length === 0)) return 0;

    const cartFilteredByOrderType =
        isStoreCounter
            ?   cartTarget.filter((product: any) => (product.id === productId || (product.idParentProduct && product.idParentProduct === productId)))
            :   cartTarget.filter((product: any) => (product.product_id === productId || (product.idParentProduct && product.idParentProduct === productId)))

    return cartFilteredByOrderType
        .reduce((total: number, product: any) => {
            //* Soma a quantity do próprio produto, caso seja SIMPLE
            let productTotal = product.type === PRODUCT_TYPE_COMPOSITE ? 0 : product.quantity;

            //* Se o produto tiver steps, somar a quantity de todos os complements
            if (product.steps) {
            const stepQuantity = product.steps.reduce((stepTotal: number, step: any) => {
                return stepTotal + (step.complements?.reduce((complementTotal: number, complement: any) => {
                return complementTotal + complement.quantity;
                }, 0) || 0);
            }, 0);

            //* Soma o total de complements ao total do produto
            productTotal += stepQuantity;
            }

        return total + productTotal;
    }, 0);
}

export const isArraysEqual = (arr1: any, arr2: any): boolean => {
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) return false;
    }
    return true;
}

export const normalizeStringToSearch = (value: string): string => {
    return value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export const copyToClipboard = async (value: string) => {
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(value);
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = value;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
        }
        return true;
    } catch (err) {
        console.error("Failed to copy: ", err);
        return false;
    }
}

export const hideKeyboard = async () => {
    if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
    }
}

export interface StoreWorkdays {
    id: number;
    store_id: number;
    service_type: number;
    weekday: number;
    operation_from: string;
    operation_to: string;
    active: boolean;
}

export function generateOperatingHoursText(storeWorkdays: StoreWorkdays[]): string {
    const weekdays: string[] = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

    let hoursText: string = "";

    storeWorkdays.forEach(service => {
        const day = weekdays[service.weekday];
        hoursText += `<strong>${day}</strong>: ${service.operation_from.slice(0, 5)} até ${service.operation_to.slice(0, 5)}<br />`;
    });

    return hoursText;
}

export const getFirstAndLastDayOfMonth = (year?: number, month?: number) => {
    const currentDate = new Date();
    const currentYear = year || currentDate.getFullYear();
    const currentMonth = month || (currentDate.getMonth() + 1); // getMonth() retorna de 0 a 11
    const firstDay = new Date(currentYear, currentMonth - 1, 1);
    const lastDay = new Date(currentYear, currentMonth, 0);

    // 'YYYY-MM-DD'
    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    return {
        date_from: formatDate(firstDay),
        date_to: formatDate(lastDay),
    };
}

export const getPercentageDiscount = (price: number, priceFrom: number): number => {
    if (priceFrom === 0) {
        throw new Error("priceFrom não pode ser zero.");
    }
    const percent = ((price - priceFrom) / priceFrom) * 100;

    return Math.round(percent * 100) / 100 ;
}

export const compareEquals = (obj1: any, obj2: any) => {
    // Verifica se são o mesmo tipo de dado
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
        return obj1 === obj2;
    }

    // Verifica se possuem o mesmo número de chaves
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    // Verifica se cada chave tem o mesmo valor
    for (let key of keys1) {
        if (!compareEquals(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
}

export const convertDateToTimestamp = (dateString: string) => {
    //*dateString no formato '23/10/2024 17:43 UTC -03:00'
    const formattedDateString = dateString.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1');
    const date = new Date(formattedDateString);
    return date.getTime();
}

export const getWeekdayInPortuguese = (weekday: number, abbreviated: boolean = false): string => {
    const weekdays = abbreviated
                        ? ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']
                        : ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
    return weekdays[weekday];
}

export function isPastHours(timestamp: number, hours: number = 2): boolean {
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
      console.warn('Timestamp inválido:', timestamp);
      return false;
    }
    const currentTime = Date.now();
    const difference = currentTime - timestamp;
    return difference >= hours * 60 * 60 * 1000;
}

export function isNewProductOnCatalog(tags: any): boolean {
    return tags?.some((tag: any) => tag.slug === "newproductoncatalog");
}

export function storeIsOpenByOrderType(orderType: string, store: any): boolean {
    const cleanedOrderType = orderType?.replace(/_/g, '');
    const key = `${cleanedOrderType}_open`;
    return store[key] === true;
}

export function orderTypeAsSlug(orderType: string): string {
    if (!orderType) return '';
    const slug = orderType.replace(/[_\-\s]/g, '').toLowerCase();
    switch (slug) {
        case 'autoatendimento': return 'auto-atendimento'
        case 'storecounter': return 'auto-atendimento'
        case 'directsale': return 'papa-fila'
        case 'papafila': return 'papa-fila'
        case 'dinein': return 'dine-in'
        default: return slug;
    }
}

export function orderTypeAsSlugConvert(orderType: string): string {
    const orderTypeSlug = orderType.replace(/[_\-\s]/g, '').toLowerCase();
    switch (orderTypeSlug) {
        case 'delivery': return ORDER_TYPE_DELIVERY
        case 'takeaway': return ORDER_TYPE_TAKE_AWAY
        case 'dinein': return ORDER_TYPE_DINE_IN
        case 'scheduling': return ORDER_TYPE_SCHEDULING
        case 'storecounter':
        case 'autoatendimento': return ORDER_TYPE_STORE_COUNTER
        case 'directsale':
        case 'papafila': return ORDER_TYPE_DIRECT_SALE
        default: return '';
    }
}

export function sanitizeText(text: string) {
    return DOMPurify.sanitize(text);
}

export function formatAddressStore(store: any) {
    var address = store.address
    address += store.number && ', ' + store.number
    address += store.neighborhood && ', ' + store.neighborhood
    address += store.city && ', ' + store.city
    address += store.state && ' - ' + store.state
    address += store.zip_code && ' - CEP: ' + formatZipcode(store.zip_code)
    return address;
}

export function goScrollTo(target?: HTMLElement) {
    const box = document.getElementById('boxPaymentMethods');
    const boxHeight = box?.offsetHeight ?? 0;
    setTimeout(() => {
      const top = (target?.offsetTop ?? 0) - boxHeight - 30; // 30px de margem
      window.scrollTo({ top, behavior: 'smooth' });
    }, 50);
}

export function findTableDataById(tableCategories: any, tableId: number) {
    for (const category of tableCategories) {
        const table = category.tables.find((table: any) => table.id === tableId);
        if (table) {
            return table;
        }
    }
    return null;
}